import "./ModalRating.scss";

import { useState, useEffect } from "react";
import { FlagIcon } from "react-flag-kit";
import Dialog from "@material-ui/core/Dialog";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import FormGroup from "@mui/material/FormGroup";
import { useDispatch, useSelector } from "react-redux";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import { HiOutlineHandThumbUp, HiOutlineHandThumbDown, HiHandThumbUp, HiHandThumbDown } from "react-icons/hi2";

import simColorImg from "../../assets/images/rating/sim.png";
import naoColorImg from "../../assets/images/rating/nao.png";
import ameiColorImg from "../../assets/images/rating/amei.png";
import { decryptIfEncrypted } from "../../utils/fieldDecryptor";
import defaultUserImg from "../../assets/images/defaultUser.jpeg";
import naoNotSelected from "../../assets/images/rating/nao_notSelected.png";
import simNotSelected from "../../assets/images/rating/sim_notSelected.png";
import { sendRatings, unsetClassesForRating } from "../../store/middleware";
import ameiNotSelected from "../../assets/images/rating/amei_notSelected.png";

function ModalRating({ target, updateScreen }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userReducer);
  const { ratingCriterias, classesForRating } = useSelector((state) => state.genericReducer);

  const [ratingCriteriasInputs, setRatingCriteriasInputs] = useState(
    ratingCriterias
      .filter((criteria) => criteria.target === target && !criteria.fixed)
      .map((criteria) => {
        if (
          classesForRating[0] &&
          classesForRating[0]?.criteriasGrades &&
          classesForRating[0]?.criteriasGrades?.length > 1
        ) {
          const hasCriteria = classesForRating[0]?.criteriasGrades.filter((grade) => grade.id == criteria.id);
          return {
            value: hasCriteria?.length > 0 ? hasCriteria[0].value : 5,
            id: criteria.id,
          };
        } else {
          return {
            value: 5,
            id: criteria.id,
          };
        }
      }),
  );

  const [answer, setAnswer] = useState("");
  const [comments, setComments] = useState("");
  const [otherComments, setOtherComments] = useState("");
  const [otherOption, setOtherOption] = useState(false);
  const [answerContent, setAnswerContent] = useState("");
  const [answerConnection, setAnswerConnection] = useState("");
  const [shouldSelect, setShouldSelect] = useState(false);
  const [answerNoOptions, setAnswerNoOptions] = useState({
    expression: false,
    loud: false,
    talk: false,
    corrections: false,
    explain: false,
    other: false,
  });

  const handleComments = (data) => {
    let comment = "";

    if (data.expression) comment += "Unclear explanations make understanding difficult,";
    if (data.loud) comment += "I feel better connection with others,";
    if (data.talk) comment += "Speaks fast and doesn't correct mistakes,";
    if (data.corrections) comment += "Only has me reading without interaction,";
    if (data.explain) comment += "Speaks my native language excessively,";
    if (data.other) {
      setOtherOption(true);
      comment += "Other: " + otherComments;
    }

    if (!data.other) {
      setOtherOption(false);
      comment += "";
    }

    setAnswerNoOptions(data);
    setComments(comment);
  };

  const submitRatings = async (e) => {
    e.preventDefault();

    verifySelection();

    if (answer !== "" && answerContent !== "" && answerConnection !== "") {
      const action = classesForRating[0]?.teacherRating ? "update" : "create";
      const ratingClassObj = {
        classScheduleIds: classesForRating.map((rating) => rating.id),
        studentId: Number(classesForRating[0].studentId),
        teacherId: Number(classesForRating[0].teacherId),
        comments: answer !== "nao" ? " " : comments + otherComments,
        status: "done",
      };

      ratingClassObj[`${target}CriteriaRatings`] = ratingCriteriasInputs.map((rating) => {
        return {
          ratingCriteriaId: Number(rating.id),
          grade: Number(rating.value),
        };
      });

      await dispatch(sendRatings({ user, ratingClassObj, target, action, updateScreen }));
    }
  };

  const handleStars = (value, index) => {
    const newRatingCriteriasInputs = [...ratingCriteriasInputs];
    setAnswer(value === 5 ? "amei" : value === 4 ? "sim" : "nao");
    newRatingCriteriasInputs[index] = { ...newRatingCriteriasInputs[index], value };
    setRatingCriteriasInputs(newRatingCriteriasInputs);
    if (value !== 3) {
      setComments("");
      setOtherComments("");
    }
  };

  const handleContent = (value, index) => {
    const newRatingCriteriasInputs = [...ratingCriteriasInputs];
    setAnswerContent(value === 5 ? "goodContent" : "badContent");
    newRatingCriteriasInputs[index] = { ...newRatingCriteriasInputs[index], value };
    setRatingCriteriasInputs(newRatingCriteriasInputs);
  };

  const handleConection = (value, index) => {
    const newRatingCriteriasInputs = [...ratingCriteriasInputs];
    setAnswerConnection(value === 5 ? "goodConnection" : "badConnection");
    newRatingCriteriasInputs[index] = { ...newRatingCriteriasInputs[index], value };
    setRatingCriteriasInputs(newRatingCriteriasInputs);
  };

  const verifySelection = () => {
    if (answer === "") {
      setShouldSelect(true);
    } else {
      setShouldSelect(false);
    }
    if (answerContent === "") {
      setShouldSelect(true);
    } else {
      setShouldSelect(false);
    }
    if (answerConnection === "") {
      setShouldSelect(true);
    } else {
      setShouldSelect(false);
    }
    if (answer === "nao") {
      if (
        answerNoOptions.expression === false &&
        answerNoOptions.loud === false &&
        answerNoOptions.talk === false &&
        answerNoOptions.corrections === false &&
        answerNoOptions.explain === false &&
        answerNoOptions.other === false
      ) {
        setShouldSelect(true);
      } else {
        setShouldSelect(false);
      }
      if (answerContent === "") {
        setShouldSelect(true);
      } else {
        setShouldSelect(false);
      }
      if (answerConnection === "") {
        setShouldSelect(true);
      } else {
        setShouldSelect(false);
      }
    }
  };

  const getClassesRating = () => {
    if (newclassesForRating[0]?.classesRatingDetail && newclassesForRating[0]?.teacherRating?.status === "done") {
      const comments = newclassesForRating[0]?.classesRatingDetail[0].comments;
      const teacherCriteriaRatings = newclassesForRating[0]?.classesRatingDetail[0].teacherCriteriaRatings;

      setAnswer(
        teacherCriteriaRatings.find((item) => item.ratingCriteriaId === 1)?.grade === 5
          ? "amei"
          : teacherCriteriaRatings.find((item) => item.ratingCriteriaId === 1)?.grade === 4
          ? "sim"
          : "nao",
      );
      setAnswerContent(
        teacherCriteriaRatings.find((item) => item.ratingCriteriaId === 2)?.grade === 5 ? "goodContent" : "badContent",
      );
      setAnswerConnection(
        teacherCriteriaRatings.find((item) => item.ratingCriteriaId === 3)?.grade === 5
          ? "goodConnection"
          : "badConnection",
      );
      setComments(comments);
      if (comments !== " ") {
        let comment = comments;
        let commentArray = comment?.split(",");
        let commentOtherArray = comment?.split(": ");
        setAnswerNoOptions({
          expression: commentArray?.includes("Unclear explanations make understanding difficult") ? true : false,
          loud: commentArray?.includes("I feel better connection with others") ? true : false,
          talk: commentArray?.includes("Speaks fast and doesn't correct mistakes") ? true : false,
          corrections: commentArray?.includes("Only has me reading without interaction") ? true : false,
          explain: commentArray?.includes("Speaks my native language excessively") ? true : false,
          other: commentArray?.includes("Other: " + commentOtherArray[1]) && commentOtherArray[1] !== "" ? true : false,
        });
        if (commentOtherArray[1]) {
          setOtherOption(true);
          setOtherComments(commentOtherArray[1]);
        }
      }
    } else {
      setAnswer("");
      setComments("");
      setOtherComments("");
      setAnswerContent("");
      setAnswerNoOptions({
        expression: false,
        loud: false,
        talk: false,
        corrections: false,
        explain: false,
        other: false,
      });
    }
  };

  const listRating = classesForRating;
  const newclassesForRating = listRating.filter((val) => {
    return (
      val.teacherId != null &&
      val.status_class !== "canceled" &&
      val.status_class !== "pending" &&
      val.target !== "demo"
    );
  });

  useEffect(() => {
    getClassesRating();
    // eslint-disable-next-line
  }, []);

  return (
    newclassesForRating &&
    newclassesForRating?.length > 0 &&
    newclassesForRating[newclassesForRating?.length - 1].status === "done" && (
      <Dialog maxWidth="md" className="newDialog" open={!!newclassesForRating?.length}>
        <DialogTitle className="dialogTitle">{t("CLASS_RATING")}</DialogTitle>
        <DialogContent className="dialogContent">
          <div>
            {newclassesForRating[0]?.teacher?.user?.picture ? (
              <img
                className="dialogImage"
                alt={t("CANCEL_IMAGE")}
                src={newclassesForRating[0]?.teacher?.user?.picture}
              />
            ) : (
              <img className="dialogImage" src={defaultUserImg} alt={t("CANCEL_IMAGE")} />
            )}
          </div>

          {newclassesForRating[0]?.teacher && (
            <p className="ratingTarget">
              <span>{t(target?.toUpperCase())}: </span>
              {decryptIfEncrypted(newclassesForRating[0]?.teacher?.user?.name)}
            </p>
          )}
          <h4 className="ratingTitle">{t("RATING_MODAL_TITLE")}:</h4>
          <hr />

          <div className="classesForRating">
            <p className="classForRating">
              <FlagIcon code={newclassesForRating[0]?.lingoLanguage?.flag} size={18} /> •{" "}
              {newclassesForRating[0].timeTotal[0] + newclassesForRating[0].timeTotal[1]}
            </p>
          </div>

          <hr />
          <>
            <div className="criterias">
              {ratingCriterias?.length > 0 &&
                ratingCriterias
                  .filter(
                    (criteria) =>
                      criteria.target === target &&
                      criteria.nameEnglish !== "Attendance" &&
                      criteria.nameEnglish === "Teacher",
                  )
                  .map((criteria) => {
                    return (
                      criteria.id === 1 && (
                        <div key={JSON.stringify(criteria)} className="criteria">
                          <h4 className="criteriaTitle">{t("RATING_DESCRIPTION_TEACHER")}</h4>
                          <div className="criteriaTeacher">
                            <div onClick={() => handleStars(5, 0)} className="answerTeacher">
                              <img
                                className={shouldSelect && answer === "" ? "shouldSelect" : ""}
                                src={answer === "amei" ? ameiColorImg : ameiNotSelected}
                                alt="sim,amei"
                              />
                              <h4
                                className={
                                  shouldSelect && answer === "" ? "criteriaTitle shouldSelectText" : "criteriaTitle"
                                }
                              >
                                {t("BTN_YES_LOVE")}
                              </h4>
                            </div>
                            <div onClick={() => handleStars(4, 0)} className="answerTeacher">
                              <img
                                className={shouldSelect && answer === "" ? "shouldSelect" : ""}
                                src={answer === "sim" ? simColorImg : simNotSelected}
                                alt="sim"
                              />
                              <h4
                                className={
                                  shouldSelect && answer === "" ? "criteriaTitle shouldSelectText" : "criteriaTitle"
                                }
                              >
                                {t("BTN_YES")}
                              </h4>
                            </div>
                            <div onClick={() => handleStars(3, 0)} className="answerTeacher">
                              <img
                                className={shouldSelect && answer === "" ? "shouldSelect" : ""}
                                src={answer === "nao" ? naoColorImg : naoNotSelected}
                                alt="nao"
                              />
                              <h4
                                className={
                                  shouldSelect && answer === "" ? "criteriaTitle shouldSelectText" : "criteriaTitle"
                                }
                              >
                                {t("BTN_NO")}
                              </h4>
                            </div>
                          </div>

                          {answer === "nao" && (
                            <>
                              <div className="divRow">
                                <FormGroup row={false}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={answerNoOptions.expression}
                                        onChange={() =>
                                          handleComments({
                                            ...answerNoOptions,
                                            expression: !answerNoOptions.expression,
                                          })
                                        }
                                        sx={{
                                          color:
                                            shouldSelect &&
                                            answer === "nao" &&
                                            answerNoOptions.expression === false &&
                                            answerNoOptions.loud === false &&
                                            answerNoOptions.talk === false &&
                                            answerNoOptions.corrections === false &&
                                            answerNoOptions.explain === false &&
                                            answerNoOptions.other === false
                                              ? "#ff0000"
                                              : "#ccc",
                                          "&.Mui-checked": {
                                            color:
                                              shouldSelect &&
                                              answer === "nao" &&
                                              answerNoOptions.expression === false &&
                                              answerNoOptions.loud === false &&
                                              answerNoOptions.talk === false &&
                                              answerNoOptions.corrections === false &&
                                              answerNoOptions.explain === false &&
                                              answerNoOptions.other === false
                                                ? "#ff0000"
                                                : "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("OPTION_EXPRESSION_RATING_COMMENTS")}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={answerNoOptions.loud}
                                        onChange={() =>
                                          handleComments({ ...answerNoOptions, loud: !answerNoOptions.loud })
                                        }
                                        sx={{
                                          color:
                                            shouldSelect &&
                                            answer === "nao" &&
                                            answerNoOptions.expression === false &&
                                            answerNoOptions.loud === false &&
                                            answerNoOptions.talk === false &&
                                            answerNoOptions.corrections === false &&
                                            answerNoOptions.explain === false &&
                                            answerNoOptions.other === false
                                              ? "#ff0000"
                                              : "#ccc",
                                          "&.Mui-checked": {
                                            color:
                                              shouldSelect &&
                                              answer === "nao" &&
                                              answerNoOptions.expression === false &&
                                              answerNoOptions.loud === false &&
                                              answerNoOptions.talk === false &&
                                              answerNoOptions.corrections === false &&
                                              answerNoOptions.explain === false &&
                                              answerNoOptions.other === false
                                                ? "#ff0000"
                                                : "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("OPTION_LOUD_RATING_COMMENTS")}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={answerNoOptions.talk}
                                        onChange={() =>
                                          handleComments({ ...answerNoOptions, talk: !answerNoOptions.talk })
                                        }
                                        sx={{
                                          color:
                                            shouldSelect &&
                                            answer === "nao" &&
                                            answerNoOptions.expression === false &&
                                            answerNoOptions.loud === false &&
                                            answerNoOptions.talk === false &&
                                            answerNoOptions.corrections === false &&
                                            answerNoOptions.explain === false &&
                                            answerNoOptions.other === false
                                              ? "#ff0000"
                                              : "#ccc",
                                          "&.Mui-checked": {
                                            color:
                                              shouldSelect &&
                                              answer === "nao" &&
                                              answerNoOptions.expression === false &&
                                              answerNoOptions.loud === false &&
                                              answerNoOptions.talk === false &&
                                              answerNoOptions.corrections === false &&
                                              answerNoOptions.explain === false &&
                                              answerNoOptions.other === false
                                                ? "#ff0000"
                                                : "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("OPTION_TALK_RATING_COMMENTS")}
                                  />
                                </FormGroup>
                                <FormGroup row={false}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={answerNoOptions.corrections}
                                        onChange={() =>
                                          handleComments({
                                            ...answerNoOptions,
                                            corrections: !answerNoOptions.corrections,
                                          })
                                        }
                                        sx={{
                                          color:
                                            shouldSelect &&
                                            answer === "nao" &&
                                            answerNoOptions.expression === false &&
                                            answerNoOptions.loud === false &&
                                            answerNoOptions.talk === false &&
                                            answerNoOptions.corrections === false &&
                                            answerNoOptions.explain === false &&
                                            answerNoOptions.other === false
                                              ? "#ff0000"
                                              : "#ccc",
                                          "&.Mui-checked": {
                                            color:
                                              shouldSelect &&
                                              answer === "nao" &&
                                              answerNoOptions.expression === false &&
                                              answerNoOptions.loud === false &&
                                              answerNoOptions.talk === false &&
                                              answerNoOptions.corrections === false &&
                                              answerNoOptions.explain === false &&
                                              answerNoOptions.other === false
                                                ? "#ff0000"
                                                : "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("OPTION_CORRECTIONS_RATING_COMMENTS")}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={answerNoOptions.explain}
                                        onChange={() =>
                                          handleComments({
                                            ...answerNoOptions,
                                            explain: !answerNoOptions.explain,
                                          })
                                        }
                                        sx={{
                                          color:
                                            shouldSelect &&
                                            answer === "nao" &&
                                            answerNoOptions.expression === false &&
                                            answerNoOptions.loud === false &&
                                            answerNoOptions.talk === false &&
                                            answerNoOptions.corrections === false &&
                                            answerNoOptions.explain === false &&
                                            answerNoOptions.other === false
                                              ? "#ff0000"
                                              : "#ccc",
                                          "&.Mui-checked": {
                                            color:
                                              shouldSelect &&
                                              answer === "nao" &&
                                              answerNoOptions.expression === false &&
                                              answerNoOptions.loud === false &&
                                              answerNoOptions.talk === false &&
                                              answerNoOptions.corrections === false &&
                                              answerNoOptions.explain === false &&
                                              answerNoOptions.other === false
                                                ? "#ff0000"
                                                : "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("OPTION_EXPLAIN_RATING_COMMENTS")}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={answerNoOptions.other}
                                        onChange={() =>
                                          handleComments({ ...answerNoOptions, other: !answerNoOptions.other })
                                        }
                                        sx={{
                                          color:
                                            shouldSelect &&
                                            answer === "nao" &&
                                            answerNoOptions.expression === false &&
                                            answerNoOptions.loud === false &&
                                            answerNoOptions.talk === false &&
                                            answerNoOptions.corrections === false &&
                                            answerNoOptions.explain === false &&
                                            answerNoOptions.other === false
                                              ? "#ff0000"
                                              : "#ccc",
                                          "&.Mui-checked": {
                                            color:
                                              shouldSelect &&
                                              answer === "nao" &&
                                              answerNoOptions.expression === false &&
                                              answerNoOptions.loud === false &&
                                              answerNoOptions.talk === false &&
                                              answerNoOptions.corrections === false &&
                                              answerNoOptions.explain === false &&
                                              answerNoOptions.other === false
                                                ? "#ff0000"
                                                : "#ccc",
                                          },
                                        }}
                                      />
                                    }
                                    label={t("OPTION_OTHER_RATING_COMMENTS")}
                                  />
                                </FormGroup>
                              </div>
                              {otherOption && (
                                <>
                                  <h4>{t("COMMENTS")}</h4>
                                  <textarea
                                    rows="4"
                                    cols="50"
                                    name="comments"
                                    placeholder="Option"
                                    value={otherComments}
                                    className="ratingComment"
                                    onChange={(e) => setOtherComments(e.target.value)}
                                  />
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )
                    );
                  })}
            </div>
            <div className="othersCriterias">
              {ratingCriterias?.length > 0 &&
                ratingCriterias
                  .filter(
                    (criteria) =>
                      criteria.target === target &&
                      criteria.nameEnglish !== "Attendance" &&
                      criteria.id > 1 &&
                      criteria.id < 4,
                  )
                  .map((criteria) => {
                    return (
                      <div key={JSON.stringify(criteria)} className="criteria">
                        {criteria.id !== 1 && (
                          <>
                            {criteria.id === 2 ? (
                              <div className="criteriaTeacher">
                                <div className="answerTeacher">
                                  <h4
                                    className={
                                      shouldSelect && answerContent === ""
                                        ? "criteriaTitle shouldSelectText"
                                        : "criteriaTitle"
                                    }
                                  >
                                    {criteria[t("INDEX_KEY_STRING")]}
                                  </h4>
                                  <div>
                                    {answerContent === "goodContent" ? (
                                      <HiHandThumbUp
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleContent(5, 1)}
                                        color={shouldSelect && answerContent === "" ? "#ff0000" : "#004fff"}
                                      />
                                    ) : (
                                      <HiOutlineHandThumbUp
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleContent(5, 1)}
                                        color={shouldSelect && answerContent === "" ? "#ff0000" : "#ccc"}
                                      />
                                    )}
                                    {answerContent === "badContent" ? (
                                      <HiHandThumbDown
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleContent(3, 1)}
                                        color={shouldSelect && answerContent === "" ? "#ff0000" : "#004fff"}
                                      />
                                    ) : (
                                      <HiOutlineHandThumbDown
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleContent(3, 1)}
                                        color={shouldSelect && answerContent === "" ? "#ff0000" : "#ccc"}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="criteriaTeacher">
                                <div className="answerTeacher">
                                  <h4
                                    className={
                                      shouldSelect && answerConnection === ""
                                        ? "criteriaTitle shouldSelectText"
                                        : "criteriaTitle"
                                    }
                                  >
                                    {criteria[t("INDEX_KEY_STRING")]}
                                  </h4>
                                  <div>
                                    {answerConnection === "goodConnection" ? (
                                      <HiHandThumbUp
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleConection(5, 2)}
                                        color={shouldSelect && answerConnection === "" ? "#ff0000" : "#004fff"}
                                      />
                                    ) : (
                                      <HiOutlineHandThumbUp
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleConection(5, 2)}
                                        color={shouldSelect && answerConnection === "" ? "#ff0000" : "#ccc"}
                                      />
                                    )}
                                    {answerConnection === "badConnection" ? (
                                      <HiHandThumbDown
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleConection(3, 2)}
                                        color={shouldSelect && answerConnection === "" ? "#ff0000" : "#004fff"}
                                      />
                                    ) : (
                                      <HiOutlineHandThumbDown
                                        size={45}
                                        className="buttonContent"
                                        onClick={() => handleConection(3, 2)}
                                        color={shouldSelect && answerConnection === "" ? "#ff0000" : "#ccc"}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
            </div>
          </>

          <div className="dialogActions">
            {user?.role === "student" ? (
              <div className="buttons">
                <button className="rateLater" onClick={() => dispatch(unsetClassesForRating({}))}>
                  {t("CANCEL_LATER_RATE")}
                </button>
                <button className="rate" onClick={submitRatings}>
                  {t("RATE")}
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
                <button style={{ width: 100, marginBottom: 15 }} onClick={() => dispatch(unsetClassesForRating({}))}>
                  {t("BTN_CLOSE")}
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  );
}

export default ModalRating;
